import apiClient from '@/api/client'
import type { ReceiptThankYouBlock } from '@/lib/printCustomerReceipt'

/**
 * Fetch the receipt coupon for an order and shape it into the block
 * payload the receipt renderer consumes. The server resolves precedence:
 * a manager-issued recovery coupon (kind="recovery") takes the place of
 * the auto-minted comeback code (kind="comeback") when one was issued.
 *
 * Returns null when:
 *   • the feature is disabled in admin settings,
 *   • the order has no code (newer enabled-state, older order),
 *   • the fetch itself fails — a printer that swallows the block is
 *     better than a printer that refuses to print a paid order.
 *
 * Called from the counter's payment-completion path in parallel with
 * buildReceiptLoyaltyBlock so the network hops overlap.
 */
export async function buildReceiptThankYouBlock(
  orderId: string,
): Promise<ReceiptThankYouBlock | null> {
  try {
    const res = await apiClient.getOrderThankYouPromo(orderId)
    if (!res.success || !res.data || !res.data.enabled || !res.data.code) {
      return null
    }
    const { kind, code, message, discount_label, expires_on, min_subtotal_pkr } = res.data
    return {
      kind: kind ?? 'comeback',
      code,
      message: message ?? '',
      discountLabel: discount_label ?? '',
      expiresOn: expires_on ?? '',
      minSubtotal: min_subtotal_pkr ?? 0,
    }
  } catch {
    return null
  }
}
